import type { MouseEvent, ReactNode } from 'react';
import { forwardRef, useId, useState } from 'react';
import type { DraggableProvidedDragHandleProps } from '@hello-pangea/dnd';
import { useTranslation } from 'next-i18next/pages';

import {
  Box,
  Collapse,
  DisabledOverlay,
  Flex,
  Group,
  Paper,
  Stack,
  Text,
} from '@highlight-cp/core-ui';

import { ActionIcons } from '../action-icons';
import { AutoSaveStatus } from '../auto-save-status';
import { CardHeader } from '../card-header';
import classes from './collapsible-card.module.css';

export interface CollapsibleCardProps {
  /** Card content. Unmounted while collapsed unless `keepMounted`, so state needs one or the other. */
  children: ReactNode;
  /** Card title displayed in the header */
  title: string;
  /** Action icons rendered in the header, alongside the chevron */
  actionIcons?: ReactNode;
  /** Which end of the header the disclosure chevron sits at. */
  chevronPosition?: 'left' | 'right';
  /** Uncontrolled: initial expanded state */
  defaultExpanded?: boolean;
  /** Secondary line beneath the title */
  description?: ReactNode;
  /** Drag handle props from @hello-pangea/dnd */
  dragHandleProps?: DraggableProvidedDragHandleProps | null;
  /** Optional validation message displayed at bottom of card */
  error?: string;
  /** Footer content displayed at bottom of card, visible whether or not the card is expanded */
  footer?: ReactNode;
  /** Whether the card interactions are disabled */
  isDisabled?: boolean;
  /** Controlled: current expanded state */
  isExpanded?: boolean;
  /** Whether the card is currently saving */
  isSaving?: boolean;
  /** Keeps content mounted while collapsed — state and effects both — so it survives a toggle. */
  keepMounted?: boolean;
  /** Optional icon displayed to the left of the title */
  leftIcon?: ReactNode;
  /** Controlled: callback when expand state changes */
  onExpandChange?: (expanded: boolean) => void;
  /** Shows the auto save status */
  showAutoSaveStatus?: boolean;
  /** `ai` tints the surface with the brand wash used for AI-assisted sections */
  variant?: 'ai';
}

/**
 * ### CollapsibleCard
 *
 * @description
 * A bordered card whose content is behind a disclosure, with optional drag handle, action icons,
 * footer, auto-save status and validation message. Supports both controlled and uncontrolled state.
 *
 * The whole header row toggles, not just the chevron — but the chevron is the actual control, so
 * keyboard and assistive tech get a real button with `aria-expanded`/`aria-controls` either way.
 *
 * Children unmount while collapsed unless `keepMounted`.
 *
 * @example
 * ```tsx
 * <CollapsibleCard
 *   description={t('Only use this if you have criteria not covered in our database')}
 *   title={t('Custom Criteria (Optional)')}
 * >
 *   <IncludeExcludeCriteria />
 * </CollapsibleCard>
 * ```
 *
 * @param props - {@link CollapsibleCardProps}
 * @returns React component
 */
const CollapsibleCard = forwardRef<HTMLDivElement, CollapsibleCardProps>(
  (
    {
      actionIcons,
      children,
      chevronPosition = 'right',
      defaultExpanded = false,
      description,
      dragHandleProps = null,
      error,
      footer,
      isDisabled = false,
      isExpanded: controlledIsExpanded,
      isSaving = false,
      keepMounted = false,
      leftIcon,
      onExpandChange,
      showAutoSaveStatus = false,
      title,
      variant,
    },
    ref,
  ) => {
    const { t } = useTranslation();
    const [internalExpanded, setInternalExpanded] = useState(defaultExpanded);
    const panelId = useId();

    const isControlled = controlledIsExpanded !== undefined;
    const isExpanded = isControlled ? controlledIsExpanded : internalExpanded;

    const handleExpandChange = () => {
      const newExpanded = !isExpanded;
      if (!isControlled) {
        setInternalExpanded(newExpanded);
      }
      onExpandChange?.(newExpanded);
    };

    // Clicking the header is pointer convenience layered over the chevron, so it defers in three
    // cases: the card is disabled; the click already went through a button (the chevron, the drag
    // handle, an action icon); or it's the click that closes a drag — dnd calls `preventDefault`
    // on it, which stops native actions but not a React handler, and this card can be a drag
    // handle end to end.
    const handleHeaderClick = (event: MouseEvent<HTMLDivElement>) => {
      if (isDisabled || event.defaultPrevented) {
        return;
      }
      if (!(event.target as HTMLElement).closest('button')) {
        handleExpandChange();
      }
    };

    const toggleLabel = isExpanded
      ? t('Collapse {{name}}', { name: title })
      : t('Expand {{name}}', { name: title });

    // Swap the chevron glyph rather than flipping it with a CSS transform: the locked
    // ActionIcon takes no className.
    //
    // `disabled` matters beyond styling: DisabledOverlay only stops the pointer, so without it a
    // keyboard user could still toggle a card a mouse user can't reach.
    const chevron = (
      <ActionIcons.ExpandCollapse
        ariaControls={panelId}
        ariaLabel={toggleLabel}
        c="dimmed"
        disabled={isDisabled}
        isExpanded={isExpanded}
        onClick={handleExpandChange}
        tooltip={isDisabled ? undefined : toggleLabel}
      />
    );

    // `CardHeader`'s Group is `gap="lg"`, so an always-rendered empty right slot would steal 20px
    // from the title on a left-chevron card that passes no action icons.
    const rightChevron = chevronPosition === 'right' ? chevron : null;
    const dragLabel = t('Drag to reorder {{name}}', { name: title });

    return (
      <Paper
        className={classes.root}
        mod={{ error: !!error }}
        pb="md"
        pt="sm"
        px="md"
        // Anchors DisabledOverlay. Mantine's Card sets this itself; Paper doesn't.
        pos="relative"
        radius="md"
        ref={ref}
        variant={variant}
        withBorder
      >
        <Box className={classes.header} onClick={handleHeaderClick}>
          <Group align="flex-start" gap={4} wrap="nowrap">
            {dragHandleProps ? (
              <ActionIcons.Drag
                ariaLabel={dragLabel}
                disabled={isDisabled}
                tooltip={isDisabled ? undefined : dragLabel}
                {...dragHandleProps}
              />
            ) : null}
            {leftIcon}
            {chevronPosition === 'left' ? chevron : null}

            <Box flex={1}>
              <CardHeader
                action={
                  actionIcons || rightChevron ? (
                    <ActionIcons.Flex>
                      {actionIcons}
                      {rightChevron}
                    </ActionIcons.Flex>
                  ) : undefined
                }
                description={description}
                title={title}
              />
            </Box>
          </Group>
        </Box>

        {/* Not Mantine's default `<Activity>`: that runs a hidden child's effect cleanups. */}
        <Collapse
          expanded={isExpanded}
          id={panelId}
          keepMountedMode={keepMounted ? 'display-none' : undefined}
        >
          <Stack className={isDisabled ? classes.disabledContent : undefined} gap={0} pt="sm">
            {showAutoSaveStatus && (
              <Flex justify="flex-end" mb="xs">
                <AutoSaveStatus isSaving={isSaving} />
              </Flex>
            )}
            {/* `Collapse` hides what it is given rather than dropping it, so state survives. */}
            {isExpanded || keepMounted ? children : null}
          </Stack>
        </Collapse>

        {footer && <Flex mt="xs">{footer}</Flex>}

        {error && (
          <Text
            aria-atomic
            aria-live="polite"
            c="var(--mantine-color-error)"
            mt="xs"
            role="alert"
            size="sm"
          >
            {error}
          </Text>
        )}

        {isDisabled && <DisabledOverlay />}
      </Paper>
    );
  },
);

CollapsibleCard.displayName = 'CollapsibleCard';

export default CollapsibleCard;
